import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import makeAjaxRequest from "helpers/makeAjaxRequest";
import { transformBool } from "helpers/utils";
import * as R from "ramda";
import { personSearchUrl } from "../actions/constants";

const selectPersonDetails = (state) => state.personDetails;

export const personDetailSelector = createSelector(
  [selectPersonDetails],
  (personDetails) => personDetails?.data
);

export const selectedPersonCtepId = createSelector(
  [personDetailSelector],
  (data) => data?.ctepPersonId
);

export const claimableErrorMessageSelector = createSelector(
  [selectPersonDetails],
  (personDetails) => personDetails?.claimableErrorMessage
);
export const NCTNGroupIdSelector = createSelector(
  [selectPersonDetails],
  (personDetails) => personDetails?.NCTNGroupId
);

export const statusLovSelector = createSelector(
  [selectPersonDetails],
  (personDetails) => personDetails?.statusLov
);

export const availableInstitutionsSelector = createSelector(
  [selectPersonDetails],
  (personDetails) => personDetails?.availableInstitutions || []
);
export const personGridExportTitleSelector = (state) =>
  `(${state.personDetails?.data?.ctepPersonId} - ${state.personDetails?.data?.lastName}, ${state.personDetails?.data?.firstName})`;

export const fetchPersonDetails = createAsyncThunk(
  "person/fetchPersonDetails",
  async (ctepId, { getState }) => {
    // Check if ctsuId has changed
    const { person } = getState();
    if (ctepId === person?.data?.person.ctepId) {
      return person.data; // Return the existing summary from state
    }

    // Fetch the new data
    const response = await makeAjaxRequest.fetchWithAuth(
      `${personSearchUrl}/id/${ctepId}/details`,
      {}
    );
    const data = transformBool(response);
    return data;
  }
);

export const getInstitutionClaimable = createAsyncThunk(
  "affiliations/getInstitutionClaimable",
  async ({ ctsuId, userGroup }, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.fetchWithAuth(
        `${personSearchUrl}/id/${ctsuId}/rosters/${userGroup}/claimable`
      );
      if (response.status === "success") {
        return response;
      } else {
        const errorMsg = R.pathOr([], ["messages"], response)
          .map(({ message }) => message)
          .join("\n");
        return rejectWithValue(errorMsg);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchClaimAffiliation = createAsyncThunk(
  "affiliations/fetchClaimAffiliation",
  async ({ personId, irostId, userGroup }, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.fetchWithAuth(
        `${personSearchUrl}/id/${personId}/irostid/${irostId}/rosters/${userGroup}/claimaffiliation`
      );
      if (R.isEmpty(response) || R.isNil(response)) {
        return [];
      } else {
        return response;
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getNCTNGroupId = createAsyncThunk(
  "person/getNctnGroupId",
  async ({ id, userGroup }, { rejectWithValue }) => {
    if (id) {
      try {
        const res = await makeAjaxRequest.fetchWithAuth(
          `${personSearchUrl}/groups/id/${id}/infoSource/${userGroup}`
        );
        return res || {};
      } catch (err) {
        if (err?.error?.includes("NCTN Group Person Id")) {
          console.log(err);
        }
        return rejectWithValue(err);
      }
    }
  }
);
// change the paramters ctsuId to ctepId in api call
export const getInstitutionList = createAsyncThunk(
  "person/getInstitutionListApi",
  async ({ memRStatus, ctsuId, userGroup }, { rejectWithValue }) => {
    if (!memRStatus) {
      return [];
    }
    try {
      const requestObj = {
        siteCtepId: null,
        ctsuId,
        infoSource: userGroup,
        memRoleStatus: memRStatus
      };

      const response = await makeAjaxRequest.postWithAuth(`${personSearchUrl}/rosters/selectinstitutions`, requestObj);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const personDetailSlice = createSlice({
  name: "person",
  initialState: {
    data: {},
    isLoading: false,
    error: null,
    claimableErrorMessage: "",
    NCTNGroupId: {},
    statusLov: [],
    availableInstitutions: []
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPersonDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPersonDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchPersonDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(getInstitutionClaimable.fulfilled, (state) => {
        state.status = "succeeded";
        state.claimableErrorMessage = "";
      })
      .addCase(getInstitutionClaimable.rejected, (state, action) => {
        state.status = "failed";
        state.claimableErrorMessage = action.payload;
      });
    builder.addCase(getNCTNGroupId.fulfilled, (state, action) => {
      state.NCTNGroupId = action.payload;
    });
    builder.addCase(fetchClaimAffiliation.fulfilled, (state, action) => {
      state.statusLov = action.payload;
    });
    builder.addCase(getInstitutionList.fulfilled, (state, action) => {
      state.availableInstitutions = action.payload;
    });
    builder.addCase(getInstitutionList.rejected, (state) => {
      state.availableInstitutions = [];
    });
  }
});

export default personDetailSlice.reducer;
